import { Group, Mesh, MeshStandardMaterial } from 'three';
import { bodyGeometry, sideGeometry } from './bodyGeometry';
import { paintFront, paintRear, paintWindscreen } from './endPainter';
import { bumpersAndMirrors, roofRack, wheels } from './extras';
import { profilePoint, segmentQuad } from './quads';
import { paintSide } from './sidePainter';
import type { Livery, VehicleSpec } from './types';

export interface BuildOptions {
  livery: Livery;
  yellowPlate?: boolean;
  seed?: number;
}

function sidePanel(spec: VehicleSpec, livery: Livery, facing: 'right' | 'left'): Mesh {
  const mesh = new Mesh(sideGeometry(spec, facing), new MeshStandardMaterial({ map: paintSide(spec, livery, facing), roughness: 0.38, metalness: 0.12 }));
  mesh.castShadow = true;
  return mesh;
}

/** Builds a vehicle from its side profile and livery. Nose points to -z, origin on the road at its centre. */
export function buildVehicle(spec: VehicleSpec, options: BuildOptions): Group {
  const { livery, yellowPlate = false } = options;
  const group = new Group();
  const body = new Mesh(bodyGeometry(spec), new MeshStandardMaterial({ color: livery.body, roughness: 0.45, metalness: 0.15 }));
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body, sidePanel(spec, livery, 'right'), sidePanel(spec, livery, 'left'));

  const glass = new MeshStandardMaterial({ map: paintWindscreen(livery), roughness: 0.15, metalness: 0.3 });
  group.add(segmentQuad(spec, profilePoint(spec, spec.windscreen), profilePoint(spec, spec.windscreen + 1), glass));

  const [rearBottom, rearTop] = [profilePoint(spec, 0), profilePoint(spec, 1)];
  const rear = new MeshStandardMaterial({ map: paintRear(livery, spec.rearWindow[1], spec.rearWindow[0], spec.height, yellowPlate) });
  group.add(segmentQuad(spec, rearBottom, rearTop, rear, 0.97));

  const last = spec.profile.length - 1;
  const nose = new MeshStandardMaterial({ map: paintFront(livery, yellowPlate) });
  group.add(segmentQuad(spec, profilePoint(spec, last - 1), profilePoint(spec, last), nose, 0.97));

  group.add(wheels(spec), bumpersAndMirrors(spec));
  if (spec.roofRack) group.add(roofRack(spec, options.seed ?? 1));
  return group;
}
